import { db, getMeta, META_KEYS } from "../../core/data/local/db";
import { initialiseCounter } from "../../core/data/local/repositories";

/**
 * Order numbers (T028, FR-221–FR-225, specs/002-barista-order-tickets/research.md R5).
 *
 * REUSES `db.counters` — the same gap-free, atomically-advanced `series →
 * nextSequence` table document numbers already use — rather than inventing a
 * second numbering mechanism. The only new thing is the series KEY: scoped to
 * the shift (`ticket-order:{shiftId}`) rather than assigned once forever, so
 * a fresh shift is a fresh series and numbering restarts without anyone
 * having to reset anything (FR-224).
 *
 * NEVER TOUCHES A DOCUMENT-NUMBER SERIES. `commitAtomically`'s own
 * `advanceCounter` path is what document numbers use; this is a separate,
 * independent series so voiding an order can never perturb the compliance-
 * critical document sequence.
 */

function seriesFor(shiftId: string): string {
  return `ticket-order:${shiftId}`;
}

/** Called on `shift.opened`. A fresh series always starts at 1; calling this
 * again for a shift already in progress is a no-op (`initialiseCounter`'s own
 * guarantee), so a config refresh mid-shift can never roll the count back. */
export async function startOrderNumbering(shiftId: string): Promise<void> {
  await initialiseCounter(seriesFor(shiftId), 1);
}

/**
 * Allocate and advance the next order number.
 *
 * SELF-INITIALISING (not just self-defaulting): if this shift's series was
 * never started — the module was enabled mid-shift, or `shift.opened` never
 * reached it — the first call here still begins at 1 rather than throwing or
 * producing an undefined-looking number. `startOrderNumbering` becomes an
 * optimisation, not a precondition.
 */
export async function nextOrderNumber(shiftId: string): Promise<string> {
  const series = seriesFor(shiftId);
  const terminalSeries = (await getMeta<string>(META_KEYS.series)) ?? "T00";

  const sequence = await db.transaction("rw", db.counters, async () => {
    const current = await db.counters.get(series);
    const next = current?.nextSequence ?? 1;
    await db.counters.put({ series, nextSequence: next + 1 });
    return next;
  });

  return `${terminalSeries}-${sequence}`;
}
